import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  Typography,
  Button,
  Avatar,
  IconButton,
  Chip,
  Snackbar,
  useTheme,
  Badge,
} from "@mui/material";
import {
  Close,
  ArrowForwardIos,
  ArrowBackIos,
  Check,
  Business,
  Email,
  LinkedIn,
  Public,
  Description,
  Store,
  Work,
} from "@mui/icons-material";
import { useMutation, gql } from "@apollo/client";
import { useSwipeable } from "react-swipeable";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import MDEditor from "@uiw/react-md-editor";
import i18n from "../i18n";

const ADD_CONNECTION = gql`
  mutation AddToConnection($attendeeId: ID!, $attendeeRole: String) {
    addToConnection(attendeeId: $attendeeId, attendeeRole: $attendeeRole) {
      success
      message
    }
  }
`;

const REMOVE_CONNECTION = gql`
  mutation RemoveFromConnection($attendeeId: ID!, $attendeeRole: String) {
    removeFromConnection(attendeeId: $attendeeId, attendeeRole: $attendeeRole) {
      success
      message
    }
  }
`;

const AttendeeModal = ({
  refresh,
  open,
  onClose,
  attendee,
  attendees,
  role,
  user,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [addConnection] = useMutation(ADD_CONNECTION);
  const [removeConnection] = useMutation(REMOVE_CONNECTION);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isEndReached, setIsEndReached] = useState(false);
  const [isConnected, setIsConnected] = useState(attendee.isConnected);
  const [currentAttendee, setCurrentAttendee] = useState(attendee);
  const [direction, setDirection] = useState("next");
  const [filteredAttendees, setFilteredAttendees] = useState(attendees);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const path = window.location.pathname;
  // console.log("attendee", attendee);
  useEffect(() => {
    if (open) {
      const index = filteredAttendees.findIndex((a) => a.id === attendee.id);
      setCurrentIndex(index >= 0 ? index : 0);
      setIsEndReached(false);
      setIsConnected(filteredAttendees[index]?.isConnected || false);
    }
  }, [open, attendee, filteredAttendees]);

  // const updateFilteredList = (removedId) => {
  //   setFilteredAttendees(prev => {
  //     const newList = prev.filter(a => a.id !== removedId);
  //     let newIndex = currentIndex;

  //     // Adjust index if needed
  //     if (newIndex >= newList.length) {
  //       newIndex = Math.max(newList.length - 1, 0);
  //     }

  //     // Update current attendee if list not empty
  //     if (newList.length > 0) {
  //       const nextAttendee = newList[Math.min(newIndex, newList.length - 1)];
  //       setCurrentAttendee(nextAttendee);
  //       setIsConnected(nextAttendee.isConnected);
  //       setCurrentIndex(Math.min(newIndex, newList.length - 1));
  //     } else {
  //       setIsEndReached(true);
  //     }

  //     return newList;
  //   });
  // };

  const handleConnectionToggle = async () => {
    try {
      if (isConnected) {
        const { data } = await removeConnection({
          variables: {
            attendeeId: currentAttendee.id,
            attendeeRole: currentAttendee.role,
          },
        });
        if (data?.removeFromConnection?.success) {
          setIsConnected(false);
          setShowRemove(true);
          setTimeout(() => {
            // updateFilteredList(currentAttendee.id);
            setShowRemove(false);
            onClose();
            refresh();
          }, 2000);
        }
      } else {
        const { data } = await addConnection({
          variables: {
            attendeeId: currentAttendee.id,
            attendeeRole: currentAttendee.role,
          },
        });
        if (data?.addToConnection?.success) {
          setIsConnected(true);
          setShowSuccess(true);
          setTimeout(() => {
            // updateFilteredList(currentAttendee.id);
            setShowSuccess(false);
            onClose();
            refresh();
          }, 2000);
        }
      }
    } catch (error) {
      console.error("Error updating connection:", error);
    }
  };

  // const handleNextAttendee = () => {
  //   setDirection("next");
  //   setCurrentIndex(prev => {
  //     const newIndex = prev + 1;
  //     if (newIndex < filteredAttendees.length) {
  //       const nextAttendee = filteredAttendees[newIndex];
  //       setCurrentAttendee(nextAttendee);
  //       setIsConnected(nextAttendee.isConnected);
  //       return newIndex;
  //     }
  //     setIsEndReached(true);
  //     return prev;
  //   });
  // };

  // const handlePrevAttendee = () => {
  //   setDirection("prev");
  //   setCurrentIndex(prev => {
  //     const newIndex = prev - 1;
  //     if (newIndex >= 0) {
  //       const prevAttendee = filteredAttendees[newIndex];
  //       setCurrentAttendee(prevAttendee);
  //       setIsConnected(prevAttendee.isConnected);
  //       return newIndex;
  //     }
  //     return prev;
  //   });
  // };

  // const handlers = useSwipeable({
  //   onSwipedLeft: handleNextAttendee,
  //   onSwipedRight: handlePrevAttendee,
  //   trackMouse: true,
  // });
  // console.log(user);

  const renderDetails = () => {
    return (
      <>
        <Typography
          variant="h6"
          textTransform={"uppercase"}
          fontWeight="bold"
          gutterBottom
        >
          {currentAttendee.name}
        </Typography>

        {currentAttendee.stallNo && (
          <Chip
            icon={<Store fontSize="small" />}
            label={`${t("attendeeModal.stallNumber")}: ${
              currentAttendee.stallNo
            }`}
            color="secondary"
            sx={{ mb: 2 }}
          />
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          {currentAttendee.designation && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Work fontSize="small" color="action" />
              <Typography color="textSecondary" textTransform={"capitalize"}>
                {currentAttendee.designation}
              </Typography>
            </Box>
          )}

          {currentAttendee.company && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Business fontSize="small" color="action" />
              <Typography color="textSecondary" textTransform={"capitalize"}>
                {currentAttendee.company}
              </Typography>
            </Box>
          )}

          {currentAttendee.email && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Email fontSize="small" color="action" />
              <Typography color="textSecondary">
                {currentAttendee.email}
              </Typography>
            </Box>
          )}

          {attendee.linkedIn && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LinkedIn fontSize="small" color="action" />
              <Typography color="textSecondary">{attendee.linkedIn}</Typography>
            </Box>
          )}

          {currentAttendee.country && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Public fontSize="small" color="action" />
              <Typography color="textSecondary" textTransform={"capitalize"}>
                {currentAttendee.country === "OM"
                  ? "Oman"
                  : currentAttendee.country}
              </Typography>
            </Box>
          )}
        </Box>

        {role !== "speaker" && (
          <Chip
            icon={<Badge fontSize="small" />}
            label={`${t(
              "attendeeModal.role"
            )}: ${currentAttendee.role.toUpperCase()}`}
            color="primary"
            sx={{ mb: 2, mt: 2, textTransform: "uppercase" }}
          />
        )}

        {currentAttendee.bio && (
          <Box
            mt={2}
            p={2}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              maxHeight: 200,
              overflowY: "auto",
              textAlign: "left",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Description fontSize="small" color="action" />
              <Typography variant="body2" fontWeight="medium">
                Bio
              </Typography>
            </Box>
            <MDEditor.Markdown
              source={currentAttendee.bio}
              style={{
                padding: 16,
                backgroundColor: "background.default",
                borderRadius: 4,
              }}
            />
          </Box>
        )}
      </>
    );
  };

  return (
    <Modal
      open={open}
      dir={i18n.dir()}
      onClose={() => {
        onClose();
        refresh();
      }}
    >
      {/* <div {...handlers} style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}> */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Snackbar
          open={showSuccess || showRemove}
          autoHideDuration={2000}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{ mt: 6 }}
        >
          <Box
            sx={{
              bgcolor: showSuccess ? "success.main" : "error.main",
              color: "white",
              p: 2,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Check fontSize="small" />
            <Typography variant="body2" sx={{ color: "white" }}>
              {showSuccess
                ? t("attendeeModal.addedSuccess")
                : t("attendeeModal.removedSuccess")}
            </Typography>
          </Box>
        </Snackbar>

        {/* {!isEndReached ? (
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: direction === "next" ? 100 : -100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: direction === "next" ? -100 : 100 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30
              }}
              style={{ position: "absolute" }}
            > */}

        <Box
          sx={{
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            mx: "auto",
            borderRadius: 4,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <IconButton
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              "&:hover": { bgcolor: "action.hover" },
            }}
            onClick={() => {
              onClose();
              refresh();
            }}
          >
            <Close />
          </IconButton>

          <Avatar
            src={currentAttendee.profile_pic}
            alt={currentAttendee.name}
            sx={{
              width: 100,
              height: 100,
              mx: "auto",
              mb: 2,
              border: `3px solid ${theme.palette.primary.main}`,
              boxShadow: 3,
              bgcolor: currentAttendee.profile_pic
                ? "transparent"
                : theme.palette.primary.main,
              color: "white",
              fontSize: 24,
              fontWeight: "bold",
            }}
          >
            {!currentAttendee.profile_pic &&
              `${currentAttendee.name.split(" ")[0][0]}${
                currentAttendee.name.split(" ")[1]
                  ? currentAttendee.name.split(" ")[1][0]
                  : ""
              }`}
          </Avatar>

          {renderDetails()}

          {user?.role !== "visitor" && (
            <Box mt={3} display="flex" gap={2} justifyContent="center">
              <Button
                variant="contained"
                color={isConnected ? "secondary" : "primary"}
                onClick={handleConnectionToggle}
                sx={{
                  flex: 1,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                }}
              >
                {isConnected
                  ? t("attendeeModal.removeFromNetwork")
                  : t("attendeeModal.addToNetwork")}
              </Button>

              {/* {path !== "/networking" && (
                    <Button
                      variant="outlined"
                      onClick={handleNextAttendee}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none'
                      }}
                    >
                      {t('attendeeModal.skip')}
                    </Button>
                  )} */}
            </Box>
          )}
          {/* <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
                  <IconButton
                    onClick={handlePrevAttendee}
                    disabled={currentIndex === 0}
                    sx={{ '&:hover': { transform: 'scale(1.1)' } }}
                  >
                    <ArrowBackIos />
                  </IconButton>

                  <Typography variant="body2" color="textSecondary">
                    {currentIndex + 1} / {filteredAttendees.length}
                  </Typography>

                  <IconButton
                    onClick={handleNextAttendee}
                    disabled={currentIndex === filteredAttendees.length - 1}
                    sx={{ '&:hover': { transform: 'scale(1.1)' } }}
                  >
                    <ArrowForwardIos />
                  </IconButton>
                </Box> */}
        </Box>
        {/* </motion.div>
          </AnimatePresence> */}
        {/* ) : (
          <Box sx={{ textAlign: "center", mt: 5 }}>
            <Typography variant="h6" gutterBottom>
              {t('attendeeModal.noMoreAttendees')}
            </Typography>
            <Button variant="contained" onClick={() => { onClose(); refresh(); }} sx={{ mt: 2 }}>
              {t('attendeeModal.close')}
            </Button>
          </Box>
        )} */}
      </div>
    </Modal>
  );
};

AttendeeModal.propTypes = {
  refresh: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  attendee: PropTypes.object.isRequired,
  attendees: PropTypes.array.isRequired,
  role: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
};

export default AttendeeModal;
